/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  ListItem,
  OrderedList,
  PasswordInput,
  TextInput,
  CopyButton
} from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useMount } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { FIELDS } from '../schema';
import { CustomSMSAuthMessages as messages } from './messages';

export const CustomAuth = ({ isQuickSetup, readOnly }: ChannelContentProps) => {
  const formMethods = useFormContext();
  const { formatMessage: t } = useIntl();
  const { registerInputField, getFieldValue, updateFieldValue } =
    useFormHelpers(formMethods);

  const byopTokenChangeState = useState<boolean>(false);
  const apiUriChangeState = useState<boolean>(false);
  const byopToken = getFieldValue(FIELDS.BYOP_VERIFICATION_TOKEN);
  const byopAPIURI = getFieldValue(FIELDS.PROVIDER_URL);

  useMount(() => {
    // If no byopAPIURI and byopToken found, it means we are in initial setup flow so auto generate and set byopToken
    // In case of edit flow UI get back PROVIDER_URL in that case don't automatically generate and
    // set byopAPIURI
    if (!byopAPIURI && !Boolean(byopToken)) {
      updateFieldValue(FIELDS.BYOP_VERIFICATION_TOKEN, uuidv4());
      // This needs to be set as empty string for initial setup as in yup schema it defaults to genesys uri
      updateFieldValue(FIELDS.PROVIDER_URL, '');
    }
  });

  return (
    <div className="TabContent__field-section">
      <OrderedList className="TabContent__subtitle GenesysAuth">
        <ListItem>
          {t(messages.byopVerificationTokenStep)}
          <div className="TabContent__spacer16" />
          <div className="SMS__custom-verification-token">
            <PasswordInput
              {...registerInputField({
                field: FIELDS.BYOP_VERIFICATION_TOKEN,
                isPasswordField: true,
                isQuickSetup,
                labelText: messages.verificationTokenLabel,
                placeholderText: messages.verificationTokenPlaceholder,
                changeState: byopTokenChangeState,
                readOnly
              })}
            />
            <CopyButton
              className="SMS__custom-verification-token-copy"
              onClick={() => copyContentToClipboard(byopToken)}
            />
          </div>
          <div className="TabContent__spacer16" />
        </ListItem>
        <div className="TabContent__spacer16" />
        <ListItem>
          {t(messages.byopAPIURIStep)}
          <div className="TabContent__spacer16" />
          <TextInput
            {...registerInputField({
              field: FIELDS.PROVIDER_URL,
              isPasswordField: false,
              isQuickSetup,
              labelText: messages.apiUriLabel,
              placeholderText: messages.apiUriByopPlaceHolder,
              changeState: apiUriChangeState,
              readOnly
            })}
            id="custom-sms-api-uri"
          />
        </ListItem>
      </OrderedList>
    </div>
  );
};
